import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FINAL_LOGO_BASE64 } from './finalLogoBase64';

/**
 * Generates a high-fidelity, premium Template Invoice PDF for Doxez Healthcare.
 * Matches the requested template UI design with numbered section icons, sleek table styling,
 * rounded status pill badges, and clean multi-page document pagination.
 * 
 * @param {Object} lead - The full lead object from the backend.
 * @param {Array} payments - Payment transactions array for this lead.
 */
export function generateDealInvoicePDF(lead, payments = []) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // 🎨 BRANDING COLOR PALETTE
  const COLORS = {
    PRIMARY: [20, 62, 120],       // #143E78 (Doxez Navy)
    SECONDARY: [16, 185, 129],    // #10B981 (Emerald Green)
    PRIMARY_LIGHT: [235, 245, 243],// #EBF5F3 (Teal Light)
    TEXT_DARK: [15, 23, 42],      // #0F172A (Slate 900)
    TEXT_GRAY: [100, 116, 139],   // #64748B (Slate 500)
    BG_LIGHT: [248, 250, 252],    // #F8FAFC (Slate 50)
    BORDER: [226, 232, 240],      // #E2E8F0 (Slate 200)
    WHITE: [255, 255, 255],
    GREEN_BG: [236, 253, 245],    // #ECFDF5
    GREEN_TEXT: [5, 150, 105],    // #059669
    BLUE_BG: [239, 246, 255],     // #EFF6FF
    BLUE_TEXT: [37, 99, 235]      // #2563EB
  };

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  let y = margin;
  let sectionIndex = 0;

  // Helper to check page bounds
  const checkPageBreak = (neededHeight = 20) => {
    if (y + neededHeight > pageH - 18) {
      doc.addPage();
      y = margin + 5;
      return true;
    }
    return false;
  };

  // Helper to draw numbered section header bar matching the template UI
  const drawSectionHeader = (title) => {
    sectionIndex++;
    checkPageBreak(16);

    // Number Box Icon
    doc.setFillColor(...COLORS.PRIMARY);
    doc.roundedRect(margin, y, 5.5, 5.5, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...COLORS.WHITE);
    doc.text(String(sectionIndex), margin + 2.75, y + 3.9, { align: 'center' });

    // Section Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.PRIMARY);
    doc.text(title.toUpperCase(), margin + 8, y + 4.2);

    // Underline
    y += 7;
    doc.setDrawColor(...COLORS.BORDER);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageW - margin, y);
    y += 5;
  };

  // Helper to draw grid fields
  const drawField = (label, value, x, yPos, width) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.TEXT_GRAY);
    doc.text(label, x, yPos);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.TEXT_DARK);
    const cleanValue = (value !== undefined && value !== null && value !== '') ? String(value) : 'N/A';
    doc.text(cleanValue, x, yPos + 4.5, { maxWidth: width - 4 });
  };

  // ── 1. HEADER & LOGO ────────────────────────────────────────
  doc.addImage(FINAL_LOGO_BASE64, 'PNG', margin, y - 2, 52, 14);

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.TEXT_GRAY);
  doc.text('Hedena Healthcare Pvt. Ltd. | GSTIN: 21AAHCH9571K1Z9', margin, y + 15);
  doc.setFont('helvetica', 'normal');
  doc.text('Support: support@doxez.in | Helpline: +91 9692949500', margin, y + 19);

  // Invoice Title Block (Right Aligned)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...COLORS.PRIMARY);
  doc.text('SURGERY SETTLEMENT INVOICE', pageW - margin, y + 4, { align: 'right' });

  const bookingId = lead.bookingId || lead.customId || lead.leadId || lead._id?.slice(-8).toUpperCase() || 'REF-PENDING';
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.TEXT_DARK);
  doc.text(`Booking Reference ID: DOX-${bookingId}`, pageW - margin, y + 9.5, { align: 'right' });

  const invoiceDateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  doc.setFontSize(7.5);
  doc.setTextColor(...COLORS.TEXT_GRAY);
  doc.text(`Invoice Date: ${invoiceDateStr}`, pageW - margin, y + 14, { align: 'right' });

  // Status Badge
  const statusStr = (lead.status || 'COMPLETED').replace(/_/g, ' ').toUpperCase();
  doc.setFillColor(...COLORS.SECONDARY);
  doc.roundedRect(pageW - margin - 32, y + 16.5, 32, 5, 1, 1, 'F');
  doc.setTextColor(...COLORS.WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(`CASE STATUS: ${statusStr}`, pageW - margin - 16, y + 20, { align: 'center' });

  y += 27;

  // Divider Line
  doc.setDrawColor(...COLORS.BORDER);
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── 2. PATIENT & SURGICAL CASE DETAILS ───────────────────────
  drawSectionHeader('Patient & Surgical Case Details');

  const colWidth = (pageW - (margin * 2)) / 2;
  const patientName = lead.patientName || lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'N/A';
  const age = lead.patientAge || lead.age || 'N/A';
  const gender = lead.patientGender || lead.gender || 'N/A';
  const phone = lead.patientPhone || lead.phone || lead.mobile || 'N/A';
  const city = lead.city || lead.patientCity || 'N/A';
  const disease = lead.disease || lead.treatmentRequired || lead.procedure || lead.diagnosis || 'Surgical Case';
  const createdDate = lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';

  drawField('Patient Name', patientName, margin + 2, y, colWidth);
  drawField('Procedure / Treatment Required', disease, margin + colWidth, y, colWidth);
  y += 11;

  drawField('Phone Number', phone, margin + 2, y, colWidth);
  drawField('Age / Gender', `${age} yrs / ${gender}`, margin + colWidth, y, colWidth);
  y += 11;

  drawField('Location / City', city, margin + 2, y, colWidth);
  drawField('Case Registered Date', createdDate, margin + colWidth, y, colWidth);
  y += 13;

  // ── 3. CLINICAL APPROVAL & MEDICAL TEAM ──────────────────────
  drawSectionHeader('Medical Team & Doctor Approvals');

  const resolveDoctorName = (docObj, fallbackName, typeKey) => {
    if (lead[typeKey] === 'other' && fallbackName) {
      const clean = fallbackName.replace(/^(dr\.\s*)+/i, '').trim();
      return `Dr. ${clean} (External)`;
    }
    if (!docObj) return 'Assigned Doctor';
    if (docObj.user && typeof docObj.user === 'object') {
      const first = docObj.user.firstName || '';
      const last = docObj.user.lastName || '';
      const full = `${first} ${last}`.trim() || docObj.user.fullName || '';
      if (full) return `Dr. ${full.replace(/^dr\.\s*/i, '')}`;
    }
    if (docObj.name) return `Dr. ${docObj.name.replace(/^dr\.\s*/i, '')}`;
    return 'Assigned Doctor';
  };

  const surgeonNameStr = resolveDoctorName(lead.assignedSurgeon, lead.assignedSurgeonOtherName, 'surgeonType');
  const anesthesiologistNameStr = resolveDoctorName(lead.assignedAnesthesiologist, lead.assignedanesthesiologistOtherName, 'anesthesiologistType');
  const reviewDecisionStr = lead.clinicalData?.reviewDecision || 'Surgery Approved';
  const riskLevelStr = lead.clinicalData?.riskLevel || 'Low Risk';

  drawField('Operating Surgeon', surgeonNameStr, margin + 2, y, colWidth);
  drawField('Anesthesiologist', anesthesiologistNameStr, margin + colWidth, y, colWidth);
  y += 11;

  drawField('Doctor Approval Status', reviewDecisionStr, margin + 2, y, colWidth);
  drawField('Clinical Risk Assessment', riskLevelStr, margin + colWidth, y, colWidth);
  y += 11;

  if (lead.clinicalData?.reviewRemarks) {
    drawField('Doctor Review Remarks', lead.clinicalData.reviewRemarks, margin + 2, y, pageW - (margin * 2) - 4);
    y += 11;
  }

  y += 3;

  // ── 4. HOSPITAL EXECUTION & SCHEDULE ─────────────────────────
  drawSectionHeader('Hospital & Surgical Execution');

  const hospitalNameStr = lead.assignedHospital?.hospitalName || lead.assignedHospital?.name || lead.hospitalName || 'Assigned Hospital';
  const hospitalLocationStr = lead.assignedHospital?.address?.city || lead.city || 'N/A';
  const schedDateRaw = lead.schedulingData?.surgeryDate || lead.scheduledSurgeryDate || lead.surgeryDate;
  const schedDateStr = schedDateRaw ? new Date(schedDateRaw).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBD';
  const otSlotStr = lead.schedulingData?.otSlot || lead.otSlot || 'Standard OT Slot';

  drawField('Assigned Hospital', hospitalNameStr, margin + 2, y, colWidth);
  drawField('Hospital City / Branch', hospitalLocationStr, margin + colWidth, y, colWidth);
  y += 11;

  drawField('Scheduled Surgery Date', schedDateStr, margin + 2, y, colWidth);
  drawField('OT Slot / Timing', otSlotStr, margin + colWidth, y, colWidth);
  y += 13;

  // ── 5. ITEMIZED SURGERY COST & BILLING BREAKUP ───────────────
  drawSectionHeader('Surgery Cost & Commercial Breakup');

  const activeBaseItems = (lead.billingDetails?.calculatorBaseItems || []).filter(item => (Number(item.amount) || 0) > 0 || (Number(item.rate) || 0) > 0);
  const activeDeductions = (lead.billingDetails?.calculatorDeductedItems || []).filter(item => (Number(item.amount) || 0) > 0);

  const platformFeeEnabled = lead.billingDetails?.platformFeeEnabled !== undefined ? Boolean(lead.billingDetails?.platformFeeEnabled) : true;
  const gstEnabled = lead.billingDetails?.gstEnabled !== undefined ? Boolean(lead.billingDetails?.gstEnabled) : true;

  const baseTotalVal = Number(lead.billingDetails?.procedureAmount || lead.procedureAmount || 0);
  const platformFeeVal = platformFeeEnabled ? Number(lead.billingDetails?.platformFeeAmount || 0) : 0;
  const gstVal = gstEnabled ? Number(lead.billingDetails?.gstAmount || 0) : 0;
  const totalPayableVal = Number(lead.billingDetails?.totalPayableAmount !== undefined ? lead.billingDetails.totalPayableAmount : (platformFeeVal + gstVal));

  const billingTableRows = [];

  if (activeBaseItems.length > 0) {
    activeBaseItems.forEach((item, idx) => {
      let particularStr = item.name || 'Service Item';
      let calcStr = 'Fixed Rate';
      const numDays = Number(item.days) || 1;
      const numHours = Number(item.hours) || 1;
      const rateVal = Number(item.rate) || Number(item.amount) || 0;

      if (numDays > 1) {
        particularStr += ` (${numDays} Days)`;
        calcStr = `${rateVal.toLocaleString('en-IN')} x ${numDays} Days`;
      } else if (numHours > 1) {
        particularStr += ` (${numHours} Hours)`;
        calcStr = `${rateVal.toLocaleString('en-IN')} x ${numHours} Hours`;
      }

      billingTableRows.push([
        idx + 1,
        particularStr,
        calcStr,
        `${Number(item.amount || rateVal || 0).toLocaleString('en-IN')}`
      ]);
    });
  } else {
    billingTableRows.push([
      1,
      `Surgical Procedure Package (${disease})`,
      'Agreed Package Rate',
      `${baseTotalVal.toLocaleString('en-IN')}`
    ]);
  }

  activeDeductions.forEach((item) => {
    billingTableRows.push([
      '-',
      `Less Deduction: ${item.name}`,
      'Excluded Expenses',
      `- ${Number(item.amount || 0).toLocaleString('en-IN')}`
    ]);
  });

  if (platformFeeEnabled) {
    billingTableRows.push([
      '',
      'Doxez Facilitation Charges',
      `${lead.billingDetails?.platformFee || 35}% Facilitation Rate`,
      `${platformFeeVal.toLocaleString('en-IN')}`
    ]);
  } else {
    billingTableRows.push([
      '',
      'Doxez Facilitation Charges',
      'Waived / Excluded',
      '0'
    ]);
  }

  if (gstEnabled) {
    billingTableRows.push([
      '',
      'Applicable GST',
      `${lead.billingDetails?.gstPercent || 18}% GST on Facilitation Fee`,
      `${gstVal.toLocaleString('en-IN')}`
    ]);
  } else {
    billingTableRows.push([
      '',
      'Applicable GST',
      'Waived / Excluded',
      '0'
    ]);
  }

  billingTableRows.push([
    '',
    'TOTAL AMOUNT PAYABLE TO DOXEZ (Inclusive of GST)',
    '',
    `INR ${totalPayableVal.toLocaleString('en-IN')}`
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'PARTICULARS / DESCRIPTION', 'CALCULATION BASIS', 'AMOUNT (INR)']],
    body: billingTableRows,
    theme: 'grid',
    headStyles: { fillColor: COLORS.PRIMARY, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
    bodyStyles: { fontSize: 7.5, textColor: COLORS.TEXT_DARK },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { halign: 'left', fontStyle: 'bold' },
      2: { halign: 'left' },
      3: { halign: 'right', fontStyle: 'bold' }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (data) => {
      if (data.row.index === billingTableRows.length - 1) {
        data.cell.styles.fillColor = COLORS.PRIMARY_LIGHT;
        data.cell.styles.textColor = COLORS.PRIMARY;
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 8;
      }
    }
  });

  y = doc.lastAutoTable.finalY + 8;

  // ── 6. HOSPITAL PAYMENT RECORDS ──────────────────────────────
  checkPageBreak(30);
  drawSectionHeader('Hospital Payment Records');

  const hasPayments = payments && payments.length > 0;
  const paymentTableRows = hasPayments ? payments.map((pay, idx) => [
    idx + 1,
    pay.razorpayPaymentId || pay.transactionId || pay.referenceId || pay._id || 'MANUAL-REF',
    pay.paymentMethod || pay.mode || 'Razorpay / Online',
    pay.createdAt ? new Date(pay.createdAt).toLocaleDateString('en-IN') : invoiceDateStr,
    'Success',
    `${Number(pay.amount || 0).toLocaleString('en-IN')}`
  ]) : [
    [1, 'MANUAL-SETTLEMENT', 'Hospital Direct / Online', invoiceDateStr, 'Success', `${totalPayableVal.toLocaleString('en-IN')}`]
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'TRANSACTION / REF ID', 'PAYMENT METHOD', 'DATE', 'STATUS', 'AMOUNT PAID']],
    body: paymentTableRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.PRIMARY, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
    bodyStyles: { fontSize: 7.5, textColor: COLORS.TEXT_DARK, halign: 'center' },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { halign: 'left' },
      4: { halign: 'center' },
      5: { halign: 'right', fontStyle: 'bold', textColor: COLORS.TEXT_DARK }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawCell: (data) => {
      // Draw rounded green pill badge for Payment Status
      if (data.section === 'body' && data.column.index === 4) {
        const cell = data.cell;
        const badgeW = 16;
        const badgeH = 4.8;
        const badgeX = cell.x + (cell.width - badgeW) / 2;
        const badgeY = cell.y + (cell.height - badgeH) / 2;

        // Clear default text background
        doc.setFillColor(255, 255, 255);
        doc.rect(cell.x + 0.2, cell.y + 0.2, cell.width - 0.4, cell.height - 0.4, 'F');

        // Draw pill
        doc.setFillColor(...COLORS.GREEN_BG);
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 2, 2, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...COLORS.GREEN_TEXT);
        doc.text('Success', badgeX + (badgeW / 2), badgeY + 3.4, { align: 'center' });
      }
    }
  });

  y = doc.lastAutoTable.finalY + 8;

  // ── 7. STAKEHOLDER DISBURSEMENT & PAYOUT RECORD ──────────────
  checkPageBreak(35);
  drawSectionHeader('Doctor & Stakeholder Disbursement Record');

  const doctorPayouts = lead.payoutData?.doctorPayouts?.filter(p => Number(p.amount) > 0) || [];
  const payoutRows = [];

  if (doctorPayouts.length > 0) {
    doctorPayouts.forEach((p, idx) => {
      const gross = Number(p.amount || 0);
      const isTdsDisabled = Boolean(p.disableTds);
      const tdsRate = isTdsDisabled ? 0 : 10;
      const tds = Math.round(gross * (tdsRate / 100));
      const net = gross - tds;
      const roleStr = p.role === 'surgeon' ? 'Operating Surgeon' : 'Anaesthesiologist';
      let docName = p.doctorName || (p.role === 'surgeon' ? surgeonNameStr : anesthesiologistNameStr);
      docName = docName.replace(/^dr\.\s*/i, '');

      payoutRows.push([
        idx + 1,
        roleStr,
        `Dr. ${docName}`,
        `${gross.toLocaleString('en-IN')}`,
        isTdsDisabled ? '0%' : `– ${tds.toLocaleString('en-IN')}`,
        `${net.toLocaleString('en-IN')}`,
        'Completed',
        p.payoutProofUrl ? { content: 'View Proof', xLink: p.payoutProofUrl } : 'N/A'
      ]);
    });
  } else {
    payoutRows.push([
      1,
      'Surgical Facilitation Package',
      'Doxez Healthcare Network',
      `${totalPayableVal.toLocaleString('en-IN')}`,
      '0',
      `${totalPayableVal.toLocaleString('en-IN')}`,
      'Completed',
      'N/A'
    ]);
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'STAKEHOLDER ROLE', 'DOCTOR NAME', 'GROSS FEE', 'TDS (10%)', 'NET DISBURSED', 'PAYOUT STATUS', 'PROOF']],
    body: payoutRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.PRIMARY, textColor: COLORS.WHITE, fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
    bodyStyles: { fontSize: 7.5, textColor: COLORS.TEXT_DARK },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { halign: 'left', fontStyle: 'bold' },
      2: { halign: 'left' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right', fontStyle: 'bold' },
      6: { halign: 'center' },
      7: { halign: 'center', fontStyle: 'bold', textColor: COLORS.PRIMARY }
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawCell: (data) => {
      // Draw rounded blue pill badge for Payout Status
      if (data.section === 'body' && data.column.index === 6) {
        const cell = data.cell;
        const badgeW = 18;
        const badgeH = 4.8;
        const badgeX = cell.x + (cell.width - badgeW) / 2;
        const badgeY = cell.y + (cell.height - badgeH) / 2;

        // Clear default text background
        doc.setFillColor(255, 255, 255);
        doc.rect(cell.x + 0.2, cell.y + 0.2, cell.width - 0.4, cell.height - 0.4, 'F');

        // Draw pill
        doc.setFillColor(...COLORS.BLUE_BG);
        doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 2, 2, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(...COLORS.BLUE_TEXT);
        doc.text('Completed', badgeX + (badgeW / 2), badgeY + 3.4, { align: 'center' });
      }

      if (data.section === 'body' && data.column.index === 7) {
        const raw = data.cell.raw;
        if (raw && typeof raw === 'object' && raw.xLink) {
          doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: raw.xLink });
        }
      }
    }
  });

  y = doc.lastAutoTable.finalY + 10;

  // ── 8. FOOTER & PAGINATION ──────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184); // Slate 400
    doc.text(
      `Doxez by Hedena Healthcare Pvt. Ltd.  ·  This is a system-generated document  ·  Page ${i} of ${totalPages}`,
      pageW / 2,
      pageH - 8,
      { align: 'center' }
    );
  }

  // Save File
  const filename = `Doxez_Invoice_${bookingId}.pdf`;
  doc.save(filename);
}
